import { Checkbox } from '@servicetim/ui/components';
import { cn } from '@servicetim/ui/lib';
import type { ReactNode } from 'react';

export interface CheckboxListOption {
  id: string;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

export interface CheckboxListGroup {
  title: string;
  options: CheckboxListOption[];
}

export interface CheckboxListProps {
  /** Flat options, or `groups` for a sectioned list. */
  options?: CheckboxListOption[];
  groups?: CheckboxListGroup[];
  value: readonly string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  columns?: 1 | 2 | 3;
  id?: string;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

const COLUMNS = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-3',
};

/** Multi-select rendered as checkboxes (scopes, event types, project restrictions). */
export function CheckboxList({
  options,
  groups,
  value,
  onChange,
  disabled,
  columns = 1,
  id,
  className,
  ...aria
}: CheckboxListProps) {
  const selected = new Set(value);
  const toggle = (optionId: string, checked: boolean) => {
    const next = new Set(selected);
    if (checked) next.add(optionId);
    else next.delete(optionId);
    onChange([...next]);
  };
  const base = id ?? 'checkbox-list';
  const renderOptions = (list: CheckboxListOption[], prefix: string) => (
    <ul className={cn('grid gap-2', COLUMNS[columns])}>
      {list.map((option) => {
        const inputId = `${prefix}-${option.id}`;
        return (
          <li key={option.id} className="flex items-start gap-2">
            <Checkbox
              id={inputId}
              checked={selected.has(option.id)}
              disabled={disabled || option.disabled}
              onCheckedChange={(checked) => toggle(option.id, checked === true)}
              className="mt-0.5"
            />
            <label htmlFor={inputId} className="flex min-w-0 cursor-pointer flex-col text-sm">
              <span className="truncate">{option.label}</span>
              {option.description ? (
                <span className="truncate text-fg-muted text-xs">{option.description}</span>
              ) : null}
            </label>
          </li>
        );
      })}
    </ul>
  );
  return (
    <fieldset
      id={id}
      className={cn('space-y-3', className)}
      aria-label={aria['aria-label']}
      aria-labelledby={aria['aria-labelledby']}
      aria-describedby={aria['aria-describedby']}
      aria-invalid={aria['aria-invalid'] || undefined}
    >
      {groups
        ? groups.map((group) => (
            <fieldset key={group.title} className="space-y-1.5">
              <legend className="mb-1 font-medium text-fg-muted text-xs uppercase tracking-wide">
                {group.title}
              </legend>
              {renderOptions(group.options, `${base}-${group.title}`)}
            </fieldset>
          ))
        : renderOptions(options ?? [], base)}
    </fieldset>
  );
}
